import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Badge, Spinner, Modal } from 'react-bootstrap';
import { ShieldLockFill, ExclamationTriangleFill, ArrowLeft, ArrowRight, Trash, Check, ChevronRight, Apple, GooglePlay } from 'react-bootstrap-icons';
import { supabase } from '../supabaseClient/supabaseClient';
import { useCart } from '../hooks/useCart';
import { useLocation, Link } from 'react-router-dom';
import { FaCcVisa, FaCcMastercard } from 'react-icons/fa';
import './SatinAl.css'; // New CSS Import

const SatinAl = () => {
  // --- STATE VE LOGIC ---
  const [user, setUser] = useState(null);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [plans, setPlans] = useState([]);

  // Global Cart Hook
  const { cart, addToCart: globalAddToCart, removeFromCart } = useCart();

  const [step, setStep] = useState(1);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [cardInfo, setCardInfo] = useState({ cardHolderName: '', cardNumber: '', expireMonth: '', expireYear: '', cvv: '' });
  const [billingInfo, setBillingInfo] = useState({ fullName: '', fullAddress: '', email: '', tcMersisNo: '' });

  // İndirim Kodu State'leri
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState({ code: '', rate: 0 }); // rate: yüzde olarak (ör: 10)
  const [discountMessage, setDiscountMessage] = useState({ type: '', text: '' });
  const [showLoginModal, setShowLoginModal] = useState(false);

  // useLocation for step management
  const location = useLocation();
  useEffect(() => {
    if (location.state?.step) {
      setStep(location.state.step);
    }
  }, [location]);

  useEffect(() => {
    const initPage = async () => {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          setUser(authUser);
          setBillingInfo(prev => ({ ...prev, email: authUser.email || '' }));

          const { data: sub } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', authUser.id)
            .eq('status', 'active')
            .gt('end_date', new Date().toISOString())
            .maybeSingle();
          if (sub) setActiveSubscription(sub);
        }
        const { data, error } = await supabase.rpc('get_service_plans');
        if (error) throw error;
        // Demo paketini gizle ve fiyata göre sırala
        const filteredPlans = (data || []).filter(plan => plan.name !== '1 Günlük Demo');
        const sortedPlans = filteredPlans.sort((a, b) => a.price - b.price);

        // Fiyatları veritabanından geldiği gibi kullanıyoruz
        const updatedPlans = sortedPlans.map(plan => ({
          ...plan,
          price_display: plan.price_display || plan.priceDisplay, // Örn: 1999.90
          price: plan.price // Örn: 2399.88 (KDV dahil)
        }));
        setPlans(updatedPlans);
      } catch (err) {
        console.error("Hata:", err.message);
        setMessage({ type: 'danger', text: 'Planlar yüklenemedi.' });
      } finally {
        setInitialLoading(false);
      }
    };
    initPage();
  }, []);

  const calculateTotal = () => {
    const total = cart.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
    return appliedDiscount.rate > 0 ? total * (1 - (appliedDiscount.rate / 100)) : total;
  };

  // --- ORTAK API URL FONKSİYONU (CORS HATALARINI ÖNLER) ---
  const getApiUrl = () => {
    let url = process.env.REACT_APP_API_URL || 'https://staibilisim.com';
    // Eğer canlı sitedeysek (www veya normal), kullanıcının o anki adresini baz alarak CORS'u tamamen bypass et
    if (window.location.hostname.includes('staibilisim.com')) {
      return window.location.origin;
    }
    // Lokal ağ testi için IP yönlendirmesi
    if (url.includes('localhost') && window.location.hostname !== 'localhost') {
      return `http://${window.location.hostname}:3000`;
    }
    return url;
  };

  // --- İNDİRİM KODU KONTROLÜ ---
  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setAppliedDiscount({ code: '', rate: 0 });
      setDiscountMessage({ type: 'danger', text: 'Lütfen bir kod giriniz.' });
      return;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/check-discount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: discountCode.trim() })
      });
      
      if (!response.ok) {
        throw new Error('Geçersiz indirim kodu.');
      }

      const data = await response.json();
      
      if (data.success) {
        setAppliedDiscount({ code: data.code, rate: data.discount_percent });
        setDiscountMessage({ type: 'success', text: `%${data.discount_percent} indirim uygulandı!` });
      } else {
        setAppliedDiscount({ code: '', rate: 0 });
        setDiscountMessage({ type: 'danger', text: 'Geçersiz indirim kodu.' });
      }
    } catch (err) {
      setAppliedDiscount({ code: '', rate: 0 });
      setDiscountMessage({ type: 'danger', text: 'Geçersiz indirim kodu.' });
    }
  };

  const addToCart = (plan) => {
    if (activeSubscription) {
      if (!window.confirm("Zaten aktif bir üyeliğiniz var. Yeni paket alımı mevcut süreyi iptal eder. Devam edilsin mi?")) return;
    }
    globalAddToCart(plan);
  };

  const handleRemoveFromCart = (planId) => {
    removeFromCart(planId);
    if (cart.length <= 1) setStep(1);
  };

  const handleCardInput = (e) => {
    const { name, value } = e.target;
    if (['cardNumber', 'cvv', 'expireMonth', 'expireYear'].includes(name) && value !== '' && !/^\d+$/.test(value)) return;
    setCardInfo({ ...cardInfo, [name]: value });
  };

  const handleFinalPayment = async () => {
    if (!user) return alert('Lütfen önce giriş yapın.');
    if (!billingInfo.email) return alert('Lütfen fatura için e-posta adresi girin.');
    if (cart[0]?.id === 6 || cart[0]?.id === '6') return alert('Demo üyelik satın alınamaz.');

    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const selectedPlan = cart[0];
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          planId: selectedPlan.id,
          cardDetails: cardInfo,
          billingDetails: billingInfo,
          email: billingInfo.email,
          amount: calculateTotal(),
          discountCode: appliedDiscount.code
        })
      });
      const data = await response.json();
      if (data.success) {
        const div = document.createElement('div');
        div.innerHTML = data.html;
        document.body.appendChild(div);
        div.querySelector('form')?.submit();
      } else throw new Error(data.error || 'Ödeme sistemi yanıt vermedi.');
    } catch (error) {
      setMessage({ type: 'danger', text: error.message });
      setLoading(false);
    }
  };

  if (initialLoading) return <div className="d-flex justify-content-center align-items-center vh-100"><Spinner animation="border" variant="primary" /></div>;

  return (
    <div className="satinal-wrapper">
      {/* HERO SECTION */}
      <div className="satinal-hero">
        <Container>
          <h1 className="satinal-title display-4">Size En Uygun Paketi Seçin</h1>
          <p className="satinal-subtitle mt-3">İhtiyaçlarınıza özel çözümlerimizle işinizi büyütün. Şeffaf fiyatlandırma, gizli ücret yok.</p>

          {/* APP İNDİRME BÖLÜMÜ */}
          <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 mb-4">
            <a href="https://apps.apple.com/us/app/miras%C4%B1m/id6759550556" target="_blank" rel="noopener noreferrer" className="btn btn-dark rounded-pill px-4 py-2 shadow d-flex align-items-center text-white text-decoration-none pb-2 pt-2">
              <Apple size={24} className="me-2" />
              <div className="text-start lh-1">
                <small style={{ fontSize: "0.65rem", display: "block", color: "#ccc" }}>App Store'dan</small>
                <strong>İndirin</strong>
              </div>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.stai.mirasim" target="_blank" rel="noopener noreferrer" className="btn btn-light rounded-pill px-4 py-2 shadow-sm d-flex align-items-center text-dark text-decoration-none pb-2 pt-2">
              <GooglePlay size={24} className="me-2" style={{ color: "#333" }} />
              <div className="text-start lh-1">
                <small style={{ fontSize: "0.65rem", display: "block", color: "#666" }}>Google Play'den</small>
                <strong>İndirin</strong>
              </div>
            </a>
          </div>

          <div className="steps-container">
            <div className={`step-badge ${step === 1 ? 'active' : 'inactive'}`}>01 PAKET SEÇİMİ</div>
            <ChevronRight className="step-arrow" size={14} />
            <div className={`step-badge ${step === 2 ? 'active' : 'inactive'}`}>02 GÜVENLİ ÖDEME</div>
          </div>
        </Container>
      </div>

      <Container fluid className="px-3 px-md-5" style={{ paddingTop: '50px', paddingBottom: '60px' }}>
        {activeSubscription && (
          <Alert variant="warning" className="shadow-sm border-0 mb-4 text-center rounded-3 p-3 mx-auto" style={{ maxWidth: 700 }}>
            <ExclamationTriangleFill className="me-2" size={20} />
            <strong>Mevcut Üyelik:</strong> Aktif bir aboneliğiniz bulunmaktadır. Yeni satın alım mevcut paketi yenileyecektir.
          </Alert>
        )}

        {message.text && <Alert variant={message.type} className="text-center shadow-sm rounded-3 mb-4 mx-auto" style={{ maxWidth: '600px' }}>{message.text}</Alert>}

        {/* --- ADIM 1: PAKET LİSTESİ --- */}
        {step === 1 && (
          <Row className="g-4 justify-content-center align-items-start">
            {/* PLANS GRID */}
            <Col lg={cart.length > 0 ? 8 : 12}>
              <Row className="g-4 justify-content-center align-items-stretch">
                {plans.map((plan, index) => {
                  const isInCart = cart.some(item => item.id === plan.id);
                  const nameParts = (plan?.full_name || "Bilinmeyen - Paket").split(' - ');
                  const mainName = nameParts[0];
                  const subName = nameParts[1] || 'Servis';
                  const isPopular = index === 1;

                  return (
                    <Col xs={12} sm={cart.length > 0 ? 12 : 6} md={6} lg={cart.length > 0 ? 6 : 4} key={plan.id} className="d-flex">
                      <div className={`pricing-card ${isPopular ? 'popular' : ''}`} style={{ width: '100%' }}>
                        {/* Popular üst aksent çizgisi */}
                        {isPopular && (
                          <div style={{
                            height: 4,
                            background: 'linear-gradient(90deg, #001f3f, #0055a5)',
                            borderRadius: '20px 20px 0 0'
                          }} />
                        )}
                        {isPopular && <div className="popular-badge" style={{ top: 4 }}>EN POPÜLER</div>}
                        <div className="card-content" style={{ paddingTop: isPopular ? '44px' : undefined }}>
                          <h3 className="plan-name">{mainName}</h3>
                          <div className="plan-sub">{subName}</div>

                          <div className="plan-price d-flex flex-column align-items-center mt-4 mb-2">
                            <div>
                              <span className="price-value">{Number(plan.price_display).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <span className="price-currency ms-1 fs-4 fw-bold text-dark">₺</span>
                            </div>
                          </div>
                          <div className="vat-text">+ %20 KDV Dahil Değildir</div>

                          <div className="features-list">
                            <div className="feature-item"><Check className="check-icon" /> Tüm Özelliklere Erişim</div>
                            <div className="feature-item"><Check className="check-icon" /> 7/24 Teknik Destek</div>
                            <div className="feature-item"><Check className="check-icon" /> Güvenli Altyapı</div>
                            {plan.description && <div className="feature-item mt-3 text-muted small fst-italic">{plan.description}</div>}
                          </div>

                          <button
                            className={`select-btn ${isInCart ? 'disabled' : (isPopular ? 'primary' : 'outline')}`}
                            onClick={() => addToCart(plan)}
                            disabled={isInCart}
                          >
                            {isInCart ? "SEPETE EKLENDİ" : "SEÇ VE DEVAM ET"}
                          </button>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Col>

            {/* CART SIDEBAR (Visible when item in cart) */}
            {cart.length > 0 && (
              <Col lg={4}>
                <div className="cart-sidebar">
                  <div className="cart-header">
                    Sipariş Özeti
                  </div>
                  <div className="cart-body">
                    {cart.map(item => (
                      <div key={item.id} className="cart-item">
                        <div>
                          <div className="fw-bold text-dark">{item.full_name?.split(' - ')[0]}</div>
                          <div className="small text-muted">{item.full_name?.split(' - ')[1]}</div>
                        </div>
                        <div className="d-flex align-items-center">
                          <span className="fw-bold me-3">{item.price} ₺</span>
                          <Trash className="text-danger" style={{ cursor: 'pointer' }} onClick={() => handleRemoveFromCart(item.id)} />
                        </div>
                      </div>
                    ))}

                    <div className="cart-total-section">
                      <div className="d-flex justify-content-between mb-2 text-muted">
                        <span>Ara Toplam</span>
                        <span>{Number(cart[0]?.price_display || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</span>
                      </div>
                      <div className="d-flex justify-content-between mb-3 text-muted">
                        <span>KDV (%20)</span>
                        <span>{(Number(cart[0]?.price || 0) - Number(cart[0]?.price_display || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</span>
                      </div>
                      <div className="d-flex justify-content-between h4 fw-bold text-dark mt-2">
                        <span>Toplam</span>
                        <span>{Number(cart[0]?.price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</span>
                      </div>

                      <Button
                        className="checkout-btn"
                        onClick={() => {
                          if (user) {
                            setStep(2);
                          } else {
                            setShowLoginModal(true);
                          }
                        }}
                      >
                        ÖDEMEYE GEÇ <ChevronRight />
                      </Button>
                    </div>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        )}
      </Container>

      {/* --- ADIM 2: ÖDEME (Existing Logic, Styled Wrapper) --- */}
      {step === 2 && (
        <Container fluid className="px-md-5 pb-5">
          <Card className="border-0 shadow-lg checkout-mega-card mx-auto overflow-hidden">
            <Row className="g-0">
              {/* SOL: Özet Paneli */}
              <Col xl={4} lg={5} md={12} className="bg-dark text-white p-3 p-md-4 p-xl-5 d-flex flex-column justify-content-between border-end border-secondary">
                <div>
                  <Button variant="link" className="text-secondary p-0 mb-4 mb-xl-5 text-decoration-none" onClick={() => setStep(1)}><ArrowLeft /> GERİ DÖN</Button>
                  <h1 className="fw-black text-info mb-3 mb-xl-4">Özet</h1>
                  <div className="bg-white bg-opacity-10 p-3 p-xl-4 rounded-4 border border-secondary border-opacity-25 mb-4 mb-xl-5">
                    <div className="small text-secondary mb-2">PAKET / SERVİS</div>
                    <h4 className="fw-bold m-0">{cart[0]?.full_name?.split(' - ')[0]}</h4>
                    <div className="text-info fw-bold mb-3 small">{cart[0]?.full_name?.split(' - ')[1]}</div>

                    <hr className="opacity-10" />

                    <div className="d-flex justify-content-between small text-secondary mb-1 align-items-center">
                      <span>Liste Fiyatı:</span>
                      <div>
                        {appliedDiscount.rate > 0 ? (
                          <>
                            <span className="text-decoration-line-through me-2 opacity-75">
                              {Number(cart[0]?.price_display || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                            </span>
                            <span className="text-success fw-bold fs-6">
                              {(Number(cart[0]?.price_display || 0) * (1 - appliedDiscount.rate / 100)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                            </span>
                          </>
                        ) : (
                          <span>{Number(cart[0]?.price_display || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex justify-content-between small text-secondary mb-3">
                      <span>Vergi:</span>
                      <span>+ %20 KDV</span>
                    </div>

                    {appliedDiscount.rate > 0 && (
                      <div className="d-flex justify-content-between small text-info mb-3 fw-bold">
                        <span>Uygulanan İndirim:</span>
                        <span>% {appliedDiscount.rate}</span>
                      </div>
                    )}

                    <div className="small text-secondary mb-1">KARTTAN ÇEKİLECEK</div>
                    <div className="fw-black text-white" style={{ fontSize: 'clamp(2rem, 5vw, 3rem)' }}>
                      {calculateTotal().toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                    </div>
                  </div>

                  {/* İndirim Kodu Alanı */}
                  <div className="mb-4">
                    <div className="small text-secondary mb-2">İNDİRİM KODU</div>
                    <Form.Group className="d-flex gap-2">
                      <Form.Control
                        type="text"
                        placeholder="İndirim Kodu (Örn: EKSTRA10)"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value)}
                        className="bg-dark text-white border-secondary"
                      />
                      <Button variant="outline-info" onClick={handleApplyDiscount}>Uygula</Button>
                    </Form.Group>
                    {discountMessage.text && (
                      <div className={`small mt-2 text-${discountMessage.type === 'danger' ? 'danger' : 'success'}`}>
                        {discountMessage.text}
                      </div>
                    )}
                  </div>

                </div>
                <div className="small opacity-75">
                  <div className="d-flex align-items-center text-success fw-bold mb-2">
                    <ShieldLockFill size={24} className="me-2" /> GÜVENLİ ÖDEME
                  </div>
                  <p className="text-secondary mb-0">Ödemeniz 256-bit SSL ile şifrelenmektedir.</p>
                </div>
              </Col>

              {/* SAĞ: Form Paneli */}
              <Col xl={8} lg={7} md={12} className="bg-white p-3 p-md-4 p-xl-5">
                <h2 className="fw-black text-dark mb-4 mb-xl-5 border-bottom pb-3">Ödeme ve Fatura Bilgileri</h2>
                <Form>
                  <Row className="g-4 g-xl-5">
                    {/* Fatura */}
                    <Col xxl={6} xl={12}>
                      <h4 className="fw-bold mb-4 text-muted fs-6 text-uppercase">1. Fatura Bilgileri</h4>
                      <Row className="g-4">
                        <Col md={12}>
                          <Form.Floating>
                            <Form.Control placeholder="Ad Soyad" onChange={(e) => setBillingInfo({ ...billingInfo, fullName: e.target.value })} />
                            <label>Fatura Sahibi / Kurum Ünvanı</label>
                          </Form.Floating>
                        </Col>
                        <Col md={12}>
                          <Form.Floating>
                            <Form.Control placeholder="TC No / Mersis No" value={billingInfo.tcMersisNo} onChange={(e) => setBillingInfo({ ...billingInfo, tcMersisNo: e.target.value })} />
                            <label>TC No / Mersis No (Opsiyonel)</label>
                          </Form.Floating>
                        </Col>
                        {/* GÜNCELLEME: E-posta alanı eklendi */}
                        <Col md={12}>
                          <Form.Floating>
                            <Form.Control type="email" placeholder="E-posta" value={billingInfo.email} onChange={(e) => setBillingInfo({ ...billingInfo, email: e.target.value })} />
                            <label>Fatura Gönderilecek E-posta</label>
                          </Form.Floating>
                        </Col>
                        <Col md={12}>
                          <Form.Floating>
                            <Form.Control as="textarea" placeholder="Adres" style={{ height: '100px' }} onChange={(e) => setBillingInfo({ ...billingInfo, fullAddress: e.target.value })} />
                            <label>Açık Fatura Adresi</label>
                          </Form.Floating>
                        </Col>
                      </Row>
                    </Col>

                    {/* Kart */}
                    <Col xxl={6} xl={12}>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="fw-bold m-0 text-muted fs-6 text-uppercase">2. Kart Bilgileri</h4>
                        <div className="d-flex gap-2 opacity-50">
                          <FaCcVisa size={30} />
                          <FaCcMastercard size={30} />
                          <img src="https://troyodeme.com/assets/images/troy-logo.svg" alt="Troy" style={{ height: '22px', marginTop: '4px' }} />
                        </div>
                      </div>
                      <Row className="g-4">
                        <Col md={6}>
                          <Form.Floating>
                            <Form.Control name="cardHolderName" placeholder="Kart Sahibi" onChange={handleCardInput} />
                            <label>Kart Üzerindeki İsim</label>
                          </Form.Floating>
                        </Col>
                        <Col md={6}>
                          <Form.Floating>
                            <Form.Control name="cardNumber" maxLength="16" placeholder="0000" onChange={handleCardInput} />
                            <label>Kart Numarası</label>
                          </Form.Floating>
                        </Col>
                        <Col xs={4}>
                          <Form.Floating><Form.Control name="expireMonth" maxLength="2" placeholder="AA" onChange={handleCardInput} /><label>Ay</label></Form.Floating>
                        </Col>
                        <Col xs={4}>
                          <Form.Floating><Form.Control name="expireYear" maxLength="2" placeholder="YY" onChange={handleCardInput} /><label>Yıl</label></Form.Floating>
                        </Col>
                        <Col xs={4}>
                          <Form.Floating><Form.Control name="cvv" maxLength="3" placeholder="CVV" onChange={handleCardInput} /><label>CVV</label></Form.Floating>
                        </Col>
                        <Col md={12}>
                          <div className="p-3 bg-light rounded-4 border">
                            <Form.Check type="checkbox" id="legal" className="small fw-bold"
                              label={
                                <span>
                                  <a href="https://staibilisim.com/sozlesme/mesafeli-satis" target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">Mesafeli Satış Sözleşmesi</a>'ni okudum ve kabul ediyorum.
                                </span>
                              }
                              onChange={(e) => setIsConfirmed(e.target.checked)}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <div className="mt-5 pt-4 border-top text-center">
                    <Button
                      variant="primary"
                      className="px-2 px-md-5 py-3 fw-black rounded-pill shadow-lg payment-final-btn"
                      disabled={loading || !isConfirmed}
                      onClick={handleFinalPayment}
                      style={{ width: '100%', maxWidth: '450px', fontSize: 'clamp(0.9rem, 3.5vw, 1.25rem)' }}
                    >
                      {loading ? <Spinner animation="border" className="me-2" size="sm" /> : `${calculateTotal().toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺ ÖDEMEYİ TAMAMLA`}
                    </Button>
                  </div>
                </Form>
              </Col>
            </Row>
          </Card>
        </Container>
      )}
      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0"></Modal.Header>
        <Modal.Body className="text-center px-4 px-md-5 pb-5">
          <div className="mb-4">
            <ShieldLockFill size={55} className="text-primary mb-3" />
            <h3 className="fw-bold text-dark">Giriş Yapmanız Gerekiyor</h3>
            <p className="text-muted mt-2">
              Satın alma işlemine devam edebilmek için lütfen hesabınıza giriş yapın veya yeni bir hesap oluşturun.
            </p>
          </div>
          <div className="d-flex flex-column gap-3">
            <Button variant="primary" size="lg" className="rounded-pill fw-bold py-3 shadow-sm" as={Link} to="/Uyegirisi?redirect=/SatinAl">
              GİRİŞ YAP
            </Button>
            <Button variant="outline-dark" size="lg" className="rounded-pill fw-bold py-3 shadow-sm" as={Link} to="/Uyeol?redirect=/SatinAl">
              YENİ HESAP OLUŞTUR
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        .payment-page-wrapper { background: #f1f5f9; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .fw-black { font-weight: 900; }
        .step-divider { width: 100px; height: 3px; background: #cbd5e1; }
        .step-divider.active { background: #0d6efd; }
        .plan-card-giant { border-radius: 30px; border: 3.5px solid transparent !important; transition: 0.3s; }
        .plan-card-giant.active { border-color: #0d6efd !important; }
        .checkout-mega-card { border-radius: 50px; max-width: 1700px; }
        .form-control { border-radius: 12px; border: 2.5px solid #e2e8f0; font-weight: 500; height: calc(3.5rem + 2px); }
        .form-control:focus { border-color: #0d6efd; box-shadow: 0 0 0 6px rgba(13,110,253,0.1); }
        textarea.form-control { height: auto !important; }
        .payment-final-btn { transition: 0.4s; letter-spacing: 1px; }
        .payment-final-btn:hover:not(:disabled) { transform: translateY(-3px); box-shadow: 0 15px 30px rgba(13,110,253,0.3) !important; }
        @media (max-width: 1200px) {
            .checkout-mega-card { border-radius: 25px; }
            .p-5 { padding: 2rem !important; }
        }
      `}</style>
    </div>
  );
};

export default SatinAl;
